import { Button, Checkbox, Grid, IconButton, Typography } from '@material-ui/core';
import React, { useEffect, useRef, useState } from 'react';
import { Row, snackbarVariants } from '../../../utilities/constants';
import {
  getPreviewInvoice,
  postSubscription,
  updateBillingInfo,
} from '../../../pages/Products/api';
import {
  attachPaymentMethod,
  getPaymentMethods,
  getStripeCustomer,
} from '../../../api/api-functions';

import BillingDetails from './BillingDetails';
import ButtonWithLoader from '../../../pure-ui/ButtonWithLoader/ButtonWithLoader';
import InvoicePreview from './InvoicePreview';
import { useHistory } from 'react-router';
import { useSnackbar } from 'notistack';

export const defaultTotal = {
  subtotal: 0,
  tax: 0,
  total: 0,
};

const toStripeBilling = (f) => ({
  name: f?.name || `${f?.firstName ?? ''} ${f?.lastName ?? ''}`.trim() || undefined,
  email: f?.email || undefined,
  phone: f?.phone || undefined,
  address: {
    line1: f?.paymentAddress || '',
    line2: f?.paymentAddress2 || undefined,
    city: f?.paymentCity || '',
    state: f?.paymentState || undefined,
    postal_code: f?.paymentZIP || '',
    country: f?.paymentCountry || '',
  },
});

const EMPTY_BILLING = {
  name: '', firstName: '', lastName: '', email: '', phone: '',
  paymentAddress: '', paymentAddress2: '', paymentCity: '',
  paymentState: '', paymentZIP: '', paymentCountry: '',
};

const mapCardToCustomerBilling = (pm) => {
  const addr = pm?.billing_details?.address || {};
  return {
    paymentAddress: addr.line1 ?? '',
    paymentCity: addr.city ?? '',
    paymentCountry: addr.country ?? '',
    paymentState: addr.state ?? '',
    paymentZIP: addr.postal_code ?? '',
  };
};

const RegistrationCheckoutForm = (props) => {
  const {
    stripe,
    elements,
    paymentElement,
    productInfo,
    paymentCoupon,
    onChangeCoupon,
    extraCertificates,
    isReturning,
  } = props;

  const router = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [isProcessing, setProcessingTo] = useState(false);
  const [userCards, setUserCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [addCard, setAddCard] = useState(false);
  const [isLoadingCard, setIsLoadingCard] = useState('');
  const [termsAndConditions, setTermsAndConditions] = useState(false);
  const [privacyPolicy, setPrivacyPolicy] = useState(false);
  const [previewInvoice, setPreviewInvoice] = useState([]);
  const [total, setTotal] = useState(defaultTotal);
  const [couponErrorMessage, setCouponErrorMessage] = useState('');
  const [validCoupon, setValidCoupon] = useState(null);
  const validCouponRef = useRef(null);
  const [billingInfo, setBillingInfo] = useState(EMPTY_BILLING);
  const [isPaymentComplete, setIsPaymentComplete] = useState(false);

  const fetchStripeDetails = async () => {
    const customer = await getStripeCustomer();
    const defaultCard = customer?.invoice_settings?.default_payment_method;
    const rawCards = await getPaymentMethods();
    const cards = Array.isArray(rawCards) ? rawCards : [];
    const sorted = [...cards].sort((a, b) => {
      if (a.id === defaultCard) return -1;
      if (b.id === defaultCard) return 1;
      return 0;
    });
    setUserCards(sorted);
    if (sorted.length > 0) {
      setSelectedCard(sorted[0].id);
      setAddCard(false);
    } else {
      setAddCard(true);
    }
  };

  useEffect(() => {
    if (isReturning) fetchStripeDetails();
  }, []);
  // const [extraCertificates, setExtraCertificates] = useState(0);

  const certQty = extraCertificates != null ? extraCertificates : 0;

  // Keep ref in sync so the productInfo effect always sees the latest validCoupon
  useEffect(() => {
    validCouponRef.current = validCoupon;
  });

  const setInvoices = (data) => {
    const { invoicesData, totalData, status, errorMessage } = data;
    if (status === 200) {
      setTotal(totalData);
      setPreviewInvoice(invoicesData);
      setCouponErrorMessage('');
      return true;
    } else if (status === 422) {
      setCouponErrorMessage('Please enter a valid coupon');
      return false;
    } else if (errorMessage) {
      setCouponErrorMessage(errorMessage);
      return false;
    }
    return false;
  };

  useEffect(() => {
    const fetchData = async () => {
      const priceIds = getPriceIds();
      if (!priceIds.length) return; // wait until productInfo is populated
      const data = await getPreviewInvoice(priceIds, validCouponRef.current ?? [], certQty);
      if (data?.errorMessage) {
        enqueueSnackbar(data.errorMessage, snackbarVariants.error);
      }
      setInvoices(data);
    };

    fetchData();
  }, [productInfo]); // eslint-disable-line

  const getPriceIds = () => {
    return productInfo.map((product) => product.priceId);
  };

  const onApplyPaymentCoupon = async (clearAll = false, codeOrList) => {
    let next;
    if (clearAll) {
      next = [];
    } else if (Array.isArray(codeOrList)) {
      next = codeOrList;
    } else {
      const code = (codeOrList ?? '').trim();
      if (!code) return { ok: false, rejectedCodes: [] };
      next = [code];
    }

    const data = await getPreviewInvoice(getPriceIds(), next, certQty);

    const serverCoupons = Array.isArray(data?.totalData?.coupons) ? data.totalData.coupons : [];
    const serverCodes = serverCoupons
      .map(c => (c?.Coupon ?? c ?? '').toString().trim())
      .filter(Boolean);

    const toLower = s => s.toLowerCase();
    const rejectedCodes = next.filter(code => !serverCodes.some(s => toLower(s) === toLower(code)));

    const isValidCoupon = setInvoices(data);
    if (isValidCoupon) {
      setValidCoupon(serverCodes.length > 0 ? serverCodes : null);
    }
    return { ok: rejectedCodes.length === 0, rejectedCodes, acceptedCodes: serverCodes, errorMessage: data.errorMessage ?? null };
  };

  const handleAddCard = async (ev) => {
    ev.preventDefault();
    setIsLoadingCard('addingCard');
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        enqueueSnackbar('Please enter valid card details.', snackbarVariants.error);
        setIsLoadingCard('');
        return;
      }
      const newCard = await stripe.createPaymentMethod({
        elements,
        params: { billing_details: toStripeBilling(billingInfo) },
      });
      await attachPaymentMethod(newCard.paymentMethod.id);
      try {
        const billing = mapCardToCustomerBilling(newCard.paymentMethod);
        await updateBillingInfo(billing);
      } catch (billingError) {
        console.warn('Billing address update failed:', billingError);
      }
      setBillingInfo(EMPTY_BILLING);
      await fetchStripeDetails();
      setSelectedCard(newCard.paymentMethod.id);
      setAddCard(false);
      setIsLoadingCard('');
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.Message || 'Failed to add card.', snackbarVariants.error);
      setIsLoadingCard('');
    }
  };

  const processSubscription = async () => {
    try {
      const qty = certQty;
      const since = new Date().toISOString();
      const processingUrl = `/Processing?since=${encodeURIComponent(since)}`;

      if (selectedCard && !addCard) {
        // Returning user with saved card
        const { clientSecret, requiresPayment } = await postSubscription(getPriceIds(), validCoupon, qty);

        const confirmFn = requiresPayment ? stripe.confirmPayment : stripe.confirmSetup;
        const { error } = await confirmFn({
          clientSecret,
          confirmParams: {
            payment_method: selectedCard,
            return_url: `${window.location.origin}${processingUrl}`,
          },
        });

        if (error) {
          enqueueSnackbar(error.message, snackbarVariants.error);
          setProcessingTo(false);
          return;
        }
      } else {
        // New card flow
        const result = await updateBillingInfo(billingInfo);
        if (!result?.IsBillingAddressValid) {
          enqueueSnackbar('Billing address is invalid', snackbarVariants.error);
          setProcessingTo(false);
          return;
        }
        const { error: submitError } = await elements.submit();
        if (submitError) {
          enqueueSnackbar(submitError?.message, snackbarVariants.error);
          setProcessingTo(false);
          return;
        }

        const { clientSecret, requiresPayment } = await postSubscription(getPriceIds(), validCoupon, qty);

        const confirmFn = requiresPayment ? stripe.confirmPayment : stripe.confirmSetup;
        const { error } = await confirmFn({
          elements,
          clientSecret,
          confirmParams: {
            payment_method_data: { billing_details: toStripeBilling(billingInfo) },
            return_url: `${window.location.origin}${processingUrl}`,
          },
        });

        if (error) {
          enqueueSnackbar(error.message, snackbarVariants.error);
          setProcessingTo(false);
          return;
        }
      }

      // No 3DS redirect — Stripe confirmed inline
      enqueueSnackbar('Registration Successful!', snackbarVariants.success);
      setTimeout(() => { router.push(processingUrl); }, 500);
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.Message, snackbarVariants.error);
      setProcessingTo(false);
    }
  };

  const handleFormSubmit = async (ev) => {
    ev.preventDefault();
    setProcessingTo(true);

    const isFreePackage = total?.total === 0;
    if (isFreePackage) {
      await postSubscription(getPriceIds(), validCoupon, certQty);
      enqueueSnackbar('Registration Successful!', snackbarVariants.success);
      sessionStorage.setItem('welcomeAllowed', 'true');
      setTimeout(() => { router.push('/Welcome'); }, 1000);
    } else {
      await processSubscription();
    }
  };

  const isButtonDisabled = isProcessing ||
    !termsAndConditions ||
    !privacyPolicy ||
    (total?.total !== 0 && (isReturning ? (!selectedCard && !isPaymentComplete) : !isPaymentComplete));

  return (
    <Grid container justifyContent="center" className="w-full">
      <div className="w-full max-w-[800px] px-4 md:px-0">
        {/* {productInfo[0].amount > 0 && (
          <div className="flex flex-col items-center bg-gray-50 p-4 rounded-lg mb-6 border border-gray-200">
            <Typography variant="h6" className="mb-2">Add Extra User Certificates?</Typography>
            <div className="flex items-center gap-4">
              <IconButton
                onClick={() => setExtraCertificates(Math.max(0, extraCertificates - 1))}
                disabled={extraCertificates <= 0} // Disable when it hits 0
                style={{ backgroundColor: '#fff', border: '1px solid #cbd5e1' }}
              >
                <Remove />
              </IconButton>

              <Typography variant="h4" style={{ fontWeight: 'bold', minWidth: '40px', textAlign: 'center' }}>
                {extraCertificates}
              </Typography>

              <IconButton
                onClick={() => setExtraCertificates(extraCertificates + 1)}
                style={{ backgroundColor: '#fff', border: '1px solid #cbd5e1' }}
              >
                +
              </IconButton>
            </div>
            <Typography variant="caption" className="text-gray-500 mt-2 text-center block">
              Your base plan already includes 3 users. <br />
              Use this to add more users beyond the included 3.
            </Typography>
          </div>
        )} */}
        <form
          onSubmit={handleFormSubmit}
          autoComplete="off"
          className="my-8 md:my-12 flex flex-col gap-6"
        >
          {/* Invoice Section */}
          <div className="w-full">
            <InvoicePreview
              previewInvoice={previewInvoice}
              paymentCoupon={paymentCoupon}
              onChangeCoupon={onChangeCoupon}
              couponErrorMessage={couponErrorMessage}
              onApplyPaymentCoupon={onApplyPaymentCoupon}
              total={total}
            />
          </div>

          {/* Payment Section */}
          {total?.total !== 0 && (
            <div className="bg-gray-50 rounded-xl p-4 md:p-8 border border-gray-100 shadow-sm">

              {isReturning ? (
                <>
                  {/* Returning user: show saved cards + optional add new card */}
                  {(addCard || userCards.length === 0) && (
                    <div>
                      <div className="payment-section bg-white border border-gray-200 rounded-lg p-4 mb-4">
                        <Typography variant="h6" className="mb-4 font-semibold text-gray-800">
                          Payment Information
                        </Typography>
                        {paymentElement && React.cloneElement(paymentElement, {
                          onChange: (event) => { setIsPaymentComplete(event.complete); },
                        })}
                      </div>
                      <BillingDetails billingInfo={billingInfo} setBillingInfo={setBillingInfo} />
                    </div>
                  )}

                  {addCard ? (
                    <div className="flex flex-col sm:flex-row justify-center gap-3 mt-3 w-full">
                      <ButtonWithLoader
                        isLoading={isLoadingCard === 'addingCard'}
                        onClick={handleAddCard}
                        title="Save Card"
                        disabled={isLoadingCard !== ''}
                        fullWidth
                      />
                      {userCards.length > 0 && (
                        <Button
                          onClick={() => { setBillingInfo(EMPTY_BILLING); setAddCard(false); }}
                          variant="outlined"
                          style={{ borderRadius: 8, borderColor: 'black', color: 'black', textTransform: 'none', width: '100%' }}
                          disabled={isLoadingCard !== ''}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  ) : (
                    userCards.length > 0 && (
                      <div className="flex w-full mb-3 justify-center">
                        <Button
                          onClick={() => setAddCard(true)}
                          variant="contained"
                          style={{ borderRadius: 8, backgroundColor: '#00A0E1', color: '#fff', textTransform: 'none' }}
                          disabled={isLoadingCard !== ''}
                        >
                          Add New Card
                        </Button>
                      </div>
                    )
                  )}

                  <div className="mt-2">
                    {userCards.map((card, index) => (
                      <div
                        key={card.id}
                        className="rounded-xl mt-3 border border-gray-300 border-solid w-full max-w-[600px] bg-white shadow-sm p-3 sm:p-4"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <div className="flex items-start sm:items-center gap-3 min-w-0 w-full">
                            <img
                              className="w-[42px] sm:w-[50px] h-auto border border-gray-300 border-solid rounded-md shrink-0"
                              src={`/images/cards/${card.card.brand}.svg`}
                              alt={card.card.brand}
                            />
                            <div className="flex flex-col min-w-0 w-full">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3 w-full">
                                <div className="text-sm sm:text-lg font-bold leading-tight">
                                  {`•••• •••• •••• ${card.card.last4}`}
                                </div>
                                {index === 0 && (
                                  <div className="text-green-600 text-xs sm:text-sm font-medium mt-1 sm:mt-0">
                                    Default
                                  </div>
                                )}
                              </div>
                              <div className="text-xs sm:text-sm text-gray-500 mt-1">
                                {card.card.brand?.toUpperCase()} • Expires {card.card.exp_month}/{card.card.exp_year}
                              </div>
                            </div>
                          </div>
                          <div className="w-full sm:w-auto">
                            <ButtonWithLoader
                              isLoading={false}
                              onClick={() => setSelectedCard(selectedCard === card.id ? null : card.id)}
                              title="Use Card"
                              disabled={isLoadingCard !== ''}
                              isProcessCompleted={selectedCard === card.id}
                              titleOnComplete="Selected"
                              fullWidth
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                /* New user: plain payment element + billing, no card management */
                <div>
                  <div className="payment-section bg-white border border-gray-200 rounded-lg p-4 mb-4">
                    <Typography variant="h6" className="mb-4 font-semibold text-gray-800">
                      Payment Information
                    </Typography>
                    {paymentElement && React.cloneElement(paymentElement, {
                      onChange: (event) => { setIsPaymentComplete(event.complete); },
                    })}
                  </div>
                  <BillingDetails billingInfo={billingInfo} setBillingInfo={setBillingInfo} />
                </div>
              )}

            </div>
          )}

          {/* Legal Checks */}
          <div className="flex flex-col gap-3 px-2">
            {/* Privacy Policy */}
            <div className="flex items-start gap-3">
              <Checkbox
                checked={privacyPolicy}
                onChange={() => setPrivacyPolicy(!privacyPolicy)}
                className="p-1 -mt-1"
                style={{
                  color: privacyPolicy ? '#10B981' : '#6B7280',
                }}
              />
              <span className="text-sm md:text-base text-gray-500 font-inter">
                I agree to the{' '}
                <a
                  href="https://www.worldwideassist.net/privacypolicy.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#006080] underline hover:text-[#004d66] transition-colors"
                >
                  Privacy Policy
                </a>
              </span>
            </div>

            {/* Terms & Conditions */}
            <div className="flex items-start gap-3">
              <Checkbox
                checked={termsAndConditions}
                onChange={() => setTermsAndConditions(!termsAndConditions)}
                className="p-1 -mt-1"
                style={{
                  color: termsAndConditions ? '#10B981' : '#6B7280',
                }}
              />
              <span className="text-sm md:text-base text-gray-500 font-inter">
                I agree to the{' '}
                <a
                  href="https://www.worldwideassist.net/terms-and-conditions.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#006080] underline hover:text-[#004d66] transition-colors"
                >
                  Terms and Conditions
                </a>
              </span>
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-4 flex justify-center w-full">
            <ButtonWithLoader
              isLoading={isProcessing}
              disabled={isButtonDisabled}
              onClick={handleFormSubmit}
              title={isProcessing ? 'Processing...' : 'Subscribe'}
              className={`
                w-full sm:w-auto min-w-[200px] h-[50px] 
                rounded-lg px-8 py-3 
                text-base font-medium normal-case transition-all duration-200
                ${isButtonDisabled
                  ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                  : 'bg-[#10B981] text-white shadow-md hover:bg-[#059669] hover:shadow-lg'
                }
              `}
            />
          </div>
        </form>
      </div>
    </Grid>
  );
};

export default RegistrationCheckoutForm;